import { NO_UI_ARRIVAL } from '@packages/types';
import type { AnonymousUiUsageEvent, TrackedUiEventName, UiArrival, UiUsageEvent } from '@packages/types';

/** Where a validated event goes, and where an event the wire schema refused is reported. */
export interface UiUsageSink {
  accept(event: UiUsageEvent): void;
  /** Receives an event of the identity-free channel: sent at once, one per request, never queued. */
  acceptAnonymous(event: AnonymousUiUsageEvent): void;
  /** `issuePaths` are zod issue paths only: never a payload value. */
  reject(name: TrackedUiEventName, issuePaths: string[]): void;
}

export interface UiUsageRuntime {
  sink: UiUsageSink;
  viewport(): 'desktop' | 'phone';
  clientVersion: string;
  /** The signup button, audience and channel this page load arrived from, fixed at boot. */
  arrival: UiArrival;
  randomUUID(): string;
}

interface InstalledUiUsage {
  runtime: UiUsageRuntime;
  visitId: string;
  seq: number;
  /** The account of the last `initial` page view: one value, so switching A to B to A records `initial` each time. */
  lastInitialAccountId: string | null;
}

const NIL_UUID = '00000000-0000-4000-8000-000000000000';

/** Drops every event and touches neither `window` nor `document`, so importing the framework is inert. */
const INERT_RUNTIME: UiUsageRuntime = {
  sink: { accept: () => undefined, acceptAnonymous: () => undefined, reject: () => undefined },
  viewport: () => 'desktop',
  clientVersion: 'localhost',
  arrival: NO_UI_ARRIVAL,
  randomUUID: () => NIL_UUID,
};

function install(runtime: UiUsageRuntime): InstalledUiUsage {
  return { runtime, visitId: runtime.randomUUID(), seq: 0, lastInitialAccountId: null };
}

let installed = install(INERT_RUNTIME);

/**
 * Installs the sink and environment fields, with a fresh visit and fresh per-install state.
 * `installUiUsage` calls it once per app boot; the test setup calls it before every test.
 */
export function configureUiUsage(runtime: UiUsageRuntime): void {
  installed = install(runtime);
}

export function currentUiUsageSink(): UiUsageSink {
  return installed.runtime.sink;
}

/** The fields the tracker stamps on every event. `seq` advances per call, so a refused event leaves a gap. */
export function nextCommonFields() {
  const { runtime, visitId } = installed;
  const seq = installed.seq;
  installed.seq += 1;
  return {
    clientEventId: runtime.randomUUID(),
    visitId,
    seq,
    viewport: runtime.viewport(),
    clientVersion: runtime.clientVersion,
  };
}

/**
 * The fields the tracker stamps on an anonymous event. Deliberately no `visitId` or `seq`, and
 * the account `seq` is untouched, so nothing links one anonymous event to another or to a visit.
 */
export function anonymousCommonFields() {
  const { runtime } = installed;
  return {
    clientEventId: runtime.randomUUID(),
    viewport: runtime.viewport(),
    ...runtime.arrival,
  };
}

export function hasRecordedInitialView(accountId: string): boolean {
  return installed.lastInitialAccountId === accountId;
}

export function markInitialViewRecorded(accountId: string): void {
  installed.lastInitialAccountId = accountId;
}
